import React, { useEffect, useState, Suspense, lazy } from 'react';
import './App.css';
import {
  Box,
  CssBaseline,
  CircularProgress,

} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/700.css';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Signup from './pages/Signup';
import EmailVerify from './pages/EmailVerify';
// CheckEmail page removed - not used
import PreviousPapers from './pages/PreviousPapers';
import SubjectPractice from './pages/SubjectPractice';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DeleteAccount from './pages/DeleteAccount';
import ResetPassword from './pages/ResetPassword';
import TermsOfService from './pages/TermsOfService';
import ContactUs from './pages/ContactUs';
import FAQ from './pages/FAQ';
import ExamFromPaper from './pages/ExamFromPaper';
import { AuthProvider, useAuth } from './context/AuthContext';
import { StudyPlansProvider } from './context/StudyPlansContext';
import TopNav from './components/TopNav';
import LandingPage from './pages/LandingPage';
import ExamsCatalog from './pages/ExamsCatalog';
import StudyPathsList from './pages/StudyPathsList';
import StudyPathOverview from './pages/StudyPathOverview';
import SubjectView from './pages/SubjectView';
import TopicView from './pages/TopicView';
import StudyPathSelector from './pages/StudyPathSelector';
import StudyPlanEdit from './pages/StudyPlanEdit';
import StudyPlanAnalytics from './pages/StudyPlanAnalytics';
import MyStudyPlans from './pages/MyStudyPlans';
import { ExamAttemptPage } from './exam';
import ErrorBoundary from './components/ErrorBoundary';
import { NetworkStatusProvider } from './components/NetworkStatus';

// Lazy load heavy pages for better initial bundle size
const ImportPapers = lazy(() => import('./pages/ImportPapers'));
const AdminSupportTickets = lazy(() => import('./pages/AdminSupportTickets'));
const GenAIPapers = lazy(() => import('./pages/GenAIPapers'));
const TrackProgress = lazy(() => import('./pages/TrackProgress'));
const ImportConfig = lazy(() => import('./pages/ImportConfig'));
const AdminAdSummary = lazy(() => import('./pages/AdminAdSummary'));
const AdminP0Dashboard = lazy(() => import('./pages/AdminP0Dashboard'));

// Loading fallback component
function PageLoader() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}
    >
      <CircularProgress />
    </Box>
  );
}

/**
 * FirebaseActionRoute — handles Firebase email action links routed to:
 *   /__/auth/action?mode=verifyEmail&oobCode=XXX   → EmailVerify
 *   /auth/action?mode=resetPassword&oobCode=XXX    → ResetPassword
 *
 * Defined OUTSIDE App() so the component reference stays stable across
 * renders and React does not unmount/remount the child on every App update.
 *
 * Vice-versa guard: if mode is explicitly "resetpassword" it goes to
 * ResetPassword; if mode is "verifyemail" it goes to EmailVerify.  Any
 * other or missing mode defaults to ResetPassword (backward-compatible
 * with password-reset deep links that may omit the mode param).
 */
function FirebaseActionRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = String(params.get('mode') || '').toLowerCase();

  if (mode === 'verifyemail') {
    return <EmailVerify />;
  }

  if (mode === 'resetpassword') {
    return <ResetPassword />;
  }

  // mode=recoverEmail restores a changed email — treat as verification flow
  if (mode === 'recoveremail') {
    return <EmailVerify />;
  }

  // Unknown / missing mode: if there is an oobCode in the URL assume it is
  // a password reset link (the most common case for direct deep links).
  // Without an oobCode there is nothing useful to show — redirect to login.
  const hasOobCode = params.has('oobCode') || params.has('code');
  return hasOobCode ? <ResetPassword /> : <Navigate to="/login" replace />;
}

export default function App() {
  function PapersPage() { return <PreviousPapers />; }

  function LearnPage() {
    return <ExamsCatalog />;
  }

  function RequireAuth({ children }) {
    const { isAuthenticated, booting } = useAuth();
    const location = useLocation();
    const currentUrl = location.pathname + location.search;
    if (booting) return null;
    if (!isAuthenticated) {
      const from = encodeURIComponent(currentUrl);
      return <Navigate to={`/login?from=${from}`} replace />;
    }
    return children;
  }

  /**
   * PublicPage — explicitly marks a route as public/unauthenticated accessible.
   * Prevents any accidental redirects to login for public pages.
   * This is for pages that should remain visible to unauthenticated users.
   */
  function PublicPage({ children }) {
    // Simply render the children - no auth checks needed
    return children;
  }

  function AdminRoute({ children }) {
    const { isAuthenticated, user, booting } = useAuth();
    const location = useLocation();
    const currentUrl = location.pathname + location.search;
    if (booting) return null;
    if (!isAuthenticated) {
      const from = encodeURIComponent(currentUrl);
      return <Navigate to={`/login?from=${from}`} replace />;
    }
    if (user?.role !== 'admin') {
      return <Navigate to="/" replace />;
    }
    return children;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NetworkStatusProvider>
          <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
            <StudyPlansProvider>
              <TopNav />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public pages — always accessible without authentication, no redirect to login */}
                  <Route path="/" element={<PublicPage><ErrorBoundary><LandingPage /></ErrorBoundary></PublicPage>} />
                  <Route path="/login" element={<PublicPage><ErrorBoundary><Login /></ErrorBoundary></PublicPage>} />
                  <Route path="/signup" element={<PublicPage><ErrorBoundary><Signup /></ErrorBoundary></PublicPage>} />
                  {/* Email verification — both canonical and legacy paths */}
                  <Route path="/auth/verify-email" element={<PublicPage><ErrorBoundary><EmailVerify /></ErrorBoundary></PublicPage>} />
                  <Route path="/verify-email" element={<PublicPage><ErrorBoundary><EmailVerify /></ErrorBoundary></PublicPage>} />
                  {/* Firebase email action handler — mode=verifyEmail → EmailVerify, mode=resetPassword → ResetPassword */}
                  <Route path="/__/auth/action" element={<PublicPage><ErrorBoundary><FirebaseActionRoute /></ErrorBoundary></PublicPage>} />
                  <Route path="/auth/action" element={<PublicPage><ErrorBoundary><FirebaseActionRoute /></ErrorBoundary></PublicPage>} />
                  <Route path="/reset-password" element={<PublicPage><ErrorBoundary><ResetPassword /></ErrorBoundary></PublicPage>} />
                  <Route path="/privacy" element={<PublicPage><ErrorBoundary><PrivacyPolicy /></ErrorBoundary></PublicPage>} />
                  <Route path="/delete-account" element={<PublicPage><ErrorBoundary><DeleteAccount /></ErrorBoundary></PublicPage>} />
                  <Route path="/terms" element={<PublicPage><ErrorBoundary><TermsOfService /></ErrorBoundary></PublicPage>} />
                  <Route path="/contact" element={<PublicPage><ErrorBoundary><ContactUs /></ErrorBoundary></PublicPage>} />
                  <Route path="/faq" element={<PublicPage><ErrorBoundary><FAQ /></ErrorBoundary></PublicPage>} />
                  <Route path="/exams" element={<RequireAuth><ErrorBoundary><ExamsCatalog /></ErrorBoundary></RequireAuth>} />
                  <Route path="/study-paths" element={<ErrorBoundary><StudyPathsList /></ErrorBoundary>} />
                  <Route path="/study-paths/mine" element={<RequireAuth><ErrorBoundary><MyStudyPlans /></ErrorBoundary></RequireAuth>} />
                  <Route path="/study-paths/select" element={<RequireAuth><ErrorBoundary><StudyPathSelector /></ErrorBoundary></RequireAuth>} />
                  <Route path="/study-paths/:pathId" element={<RequireAuth><ErrorBoundary><StudyPathOverview /></ErrorBoundary></RequireAuth>} />
                  <Route path="/study-paths/:pathId/edit" element={<RequireAuth><ErrorBoundary><StudyPlanEdit /></ErrorBoundary></RequireAuth>} />
                  <Route path="/study-paths/:pathId/analytics" element={<RequireAuth><ErrorBoundary><StudyPlanAnalytics /></ErrorBoundary></RequireAuth>} />
                  <Route path="/study-paths/:pathId/subject/:subjectId" element={<RequireAuth><ErrorBoundary><SubjectView /></ErrorBoundary></RequireAuth>} />
                  <Route path="/study-paths/:pathId/subject/:subjectId/topic/:topicId" element={<RequireAuth><ErrorBoundary><TopicView /></ErrorBoundary></RequireAuth>} />
                  <Route path="/exams/:examId/attempt" element={<RequireAuth><ErrorBoundary><ExamAttemptPage /></ErrorBoundary></RequireAuth>} />
                  <Route path="/exam/:examId/paper/:paperId" element={<RequireAuth><ErrorBoundary><ExamFromPaper /></ErrorBoundary></RequireAuth>} />
                  <Route path="/papers" element={<RequireAuth><ErrorBoundary><PapersPage /></ErrorBoundary></RequireAuth>} />
                  <Route path="/genai-papers" element={<RequireAuth><ErrorBoundary><GenAIPapers /></ErrorBoundary></RequireAuth>} />
                  <Route path="/subject-practice" element={<RequireAuth><ErrorBoundary><SubjectPractice /></ErrorBoundary></RequireAuth>} />
                  <Route path="/learn" element={<RequireAuth><ErrorBoundary><LearnPage /></ErrorBoundary></RequireAuth>} />
                  <Route path="/track-progress" element={<RequireAuth><ErrorBoundary><TrackProgress /></ErrorBoundary></RequireAuth>} />
                  <Route path="/admin/import" element={<AdminRoute><ErrorBoundary><ImportPapers /></ErrorBoundary></AdminRoute>} />
                  <Route path="/admin/support-tickets" element={<AdminRoute><ErrorBoundary><AdminSupportTickets /></ErrorBoundary></AdminRoute>} />
                  <Route path="/admin/import-config" element={<AdminRoute><ErrorBoundary><ImportConfig /></ErrorBoundary></AdminRoute>} />
                  <Route path="/admin/ads-summary" element={<AdminRoute><ErrorBoundary><AdminAdSummary /></ErrorBoundary></AdminRoute>} />
                  <Route path="/admin/p0" element={<AdminRoute><ErrorBoundary><AdminP0Dashboard /></ErrorBoundary></AdminRoute>} />
                </Routes>
              </Suspense>
            </StudyPlansProvider>
          </BrowserRouter>
        </NetworkStatusProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
